import React, { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Palette, Package, Save, Trash2, Bookmark, Share2, Wand2 } from "lucide-react";

const VIBES = [
  { id: "cozy_autumn", label: "Cozy Autumn", emoji: "🍂", grad: "from-[#b45309] to-[#f59e0b]", fx: ["🍁 Leaves", "🎬 Warm", "🌆 Dusk"] },
  { id: "spooky_night", label: "Spooky Night", emoji: "🎃", grad: "from-[#4c1d95] to-[#7c2d12]", fx: ["🌫️ Fog", "🔥 Embers", "🌙 Night"] },
  { id: "neon_rave", label: "Neon Rave", emoji: "🌆", grad: "from-[#701a75] to-[#22d3ee]", fx: ["✨ Fireflies", "🎬 Vibrant", "🌙 Night"] },
  { id: "winter_storm", label: "Winter Storm", emoji: "❄️", grad: "from-[#0e7490] to-[#93c5fd]", fx: ["🌨️ Snow", "❄️ Flurries", "🎬 Cool"] },
  { id: "spring_bloom", label: "Spring Bloom", emoji: "🌸", grad: "from-[#be185d] to-[#f9a8d4]", fx: ["🌸 Petals", "🎬 Warm", "🌅 Golden"] },
  { id: "underwater_dream", label: "Underwater", emoji: "🌊", grad: "from-[#155e75] to-[#2563eb]", fx: ["🫧 Bubbles", "🎬 Cool", "☀️ Day"] },
  { id: "firefly_meadow", label: "Fireflies", emoji: "✨", grad: "from-[#365314] to-[#a3e635]", fx: ["✨ Fireflies", "🎬 Moody", "🌆 Dusk"] },
  { id: "sunny_arcade", label: "Sunny Arcade", emoji: "🎈", grad: "from-[#2563eb] to-[#38bdf8]", fx: ["🎬 Vibrant", "☀️ Day", "🎈 Bright"] },
];
const STYLES = [
  { id: "anime", label: "Anime", emoji: "🌸" }, { id: "realistic", label: "Realistic", emoji: "📷" },
  { id: "cartoon", label: "Cartoon", emoji: "🎨" }, { id: "pixel", label: "Pixel", emoji: "🕹️" },
  { id: "voxel", label: "Voxel", emoji: "🧊" }, { id: "cyberpunk", label: "Cyberpunk", emoji: "🌆" },
  { id: "clay", label: "Claymation", emoji: "🪀" }, { id: "lowpoly", label: "Low-Poly", emoji: "🔷" },
  { id: "fantasy", label: "Fantasy", emoji: "🐉" }, { id: "watercolor", label: "Watercolor", emoji: "🖌️" },
];
const OBJECTS = [
  { id: "health_potion", label: "Potion", emoji: "🧪" }, { id: "speed_boots", label: "Speed Boots", emoji: "👟" },
  { id: "shield_gear", label: "Shield", emoji: "🛡️" }, { id: "sword", label: "Sword", emoji: "⚔️" },
  { id: "magic_bow", label: "Bow", emoji: "🏹" }, { id: "magic_staff", label: "Staff", emoji: "🪄" },
  { id: "magnet", label: "Magnet", emoji: "🧲" }, { id: "star", label: "Star", emoji: "⭐" },
  { id: "shield_pu", label: "Bubble", emoji: "🫧" }, { id: "double_points", label: "2× Points", emoji: "✖️" },
  { id: "lava", label: "Lava", emoji: "🌋" }, { id: "spikes", label: "Spikes", emoji: "🔺" },
  { id: "saw", label: "Saw", emoji: "⚙️" }, { id: "treasure", label: "Treasure", emoji: "🎁" },
  { id: "gem", label: "Gems", emoji: "💎" },
];

const GENRE_PACKS = [
  { id: "rpg", label: "RPG", emoji: "⚔️", changes: ["XP & leveling system", "A small inventory + pickups", "A friendly NPC to talk to", "A short quest with a reward"] },
  { id: "shooter", label: "Shooter", emoji: "🔫", changes: ["A projectile/shooting attack", "Waves of enemies that ramp up", "Rapid-fire & shield power-ups"] },
  { id: "racer", label: "Racer", emoji: "🏎️", changes: ["A lap / finish-line goal", "Speed-boost pads", "Obstacles to dodge", "A rival racer to beat"] },
  { id: "platformer", label: "Platformer", emoji: "🕹️", changes: ["Moving platforms", "Spikes & hazards", "A double-jump power-up", "Coins to collect"] },
  { id: "puzzle", label: "Puzzle", emoji: "🧩", changes: ["A clear objective", "Keys / switches that unlock the exit", "Trickier levels as you go"] },
  { id: "survival", label: "Survival", emoji: "🧟", changes: ["Relentless enemy waves", "Health system + pickups", "A score that climbs as you survive"] },
  { id: "tower", label: "Tower Defense", emoji: "🏰", changes: ["Lanes of incoming enemies", "Placeable defense towers", "Currency per kill", "A base to protect"] },
];

export function QuickFXTray({ gameId, onApplied }) {
  const [busy, setBusy] = useState(null);
  const [redraw, setRedraw] = useState(false);
  const [customVibes, setCustomVibes] = useState([]);
  const [vibeName, setVibeName] = useState("");
  const [savingVibe, setSavingVibe] = useState(false);
  const [shareBusy, setShareBusy] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(null);
  const [packPreview, setPackPreview] = useState(null);
  const [castBusy, setCastBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    api.get("/fx/custom-vibes").then(({ data }) => setCustomVibes(data.vibes || [])).catch(() => {});
    api.get(`/games/${gameId}/suggest-pack`).then(({ data }) => setSuggestion(data)).catch(() => {});
  }, [gameId]);

  const call = async (endpoint, payload, key) => {
    if (busy) return;
    setBusy(key);
    try {
      const { data } = await api.post(`/games/${gameId}/${endpoint}`, payload);
      if (onApplied) onApplied(data.config, data.version, data.label || "Updated");
      else toast.success(`${data.label || "Applied"} ✓`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't apply that.");
    } finally { setBusy(null); }
  };

  const saveVibe = async () => {
    setSavingVibe(true);
    try {
      const { data } = await api.post("/fx/custom-vibes", { game_id: gameId, name: vibeName.trim() || "My Vibe", emoji: "🎨" });
      setCustomVibes(data.vibes || []); setVibeName("");
      toast.success("Vibe saved — reuse it on any game! ✨");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save vibe."); }
    finally { setSavingVibe(false); }
  };

  const deleteVibe = async (id) => {
    try { const { data } = await api.delete(`/fx/custom-vibes/${id}`); setCustomVibes(data.vibes || []); }
    catch { /* ignore */ }
  };

  const shareVibe = async (id) => {
    setShareBusy(id);
    try {
      const { data } = await api.post(`/fx/custom-vibes/${id}/share`);
      const link = `${window.location.origin}/app/vibe/${data.code}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Share link copied — friends can one-tap this vibe onto their games! 🔗");
      } catch { toast.success(`Share this vibe: ${link}`); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create a share link."); }
    finally { setShareBusy(null); }
  };

  // Restyle Preview — hero (cheap) or whole-cast before/after before committing.
  const previewRestyle = async (styleId, scope = "hero") => {
    if (previewBusy || busy) return;
    setPreviewBusy(styleId);
    try {
      const { data } = await api.post(`/games/${gameId}/restyle-preview`, { style: styleId, scope }, { timeout: 180000 });
      setPreview({ style: styleId, ...data });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't draw a preview."); }
    finally { setPreviewBusy(null); }
  };
  const applyPreview = async () => {
    if (!preview || busy) return;
    if (preview.scope === "cast") {
      setBusy(`style-${preview.style}`);
      try {
        const { data } = await api.post(`/games/${gameId}/restyle-commit`,
          { config: preview.config, summary: `Restyled the whole cast to ${preview.style}` }, { timeout: 60000 });
        setPreview(null);
        if (onApplied) onApplied(data.config, data.version, data.label);
        toast.success("Whole cast restyled! ✨");
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't apply that."); }
      finally { setBusy(null); }
    } else {
      const style = preview.style;
      setPreview(null);
      await call("restyle", { style, redraw: true }, `style-${style}`);
    }
  };

  const genrePack = async (pack, key) => {
    if (busy) return;
    setPackPreview(null);
    setBusy(`genre-${key}`);
    try {
      const { data } = await api.post(`/games/${gameId}/genre-pack`, { pack }, { timeout: 120000 });
      if (onApplied) onApplied(data.config, data.version, data.summary);
      toast.success(data.summary || "Genre pack applied!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't apply that pack."); }
    finally { setBusy(null); }
  };

  const exportCastSheet = async (poster = false) => {
    if (castBusy) return;
    setCastBusy(poster ? "poster" : "sheet");
    try {
      const { data } = await api.post(`/games/${gameId}/cast-sheet`, { poster }, { timeout: 60000 });
      window.open(`${process.env.REACT_APP_BACKEND_URL}${data.url}`, "_blank");
      toast.success(`Cast ${poster ? "poster" : "line-up"} ready (${data.members} character${data.members === 1 ? "" : "s"}) — opened in a new tab! 📸`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't build the cast sheet."); }
    finally { setCastBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="quickfx-tray">
      <section>
        <h3 className="flex items-center gap-1.5 text-sm font-bold mb-2"><Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" /> Vibes</h3>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">One tap stacks weather, particles, colour grade &amp; time-of-day. Hover to preview the mood.</p>
        <div className="grid grid-cols-4 gap-1.5">
          {VIBES.map((v) => (
            <div key={v.id} className="relative group">
              <button onClick={() => call("vibe", { preset: v.id }, v.id)} disabled={!!busy} data-testid={`quickfx-vibe-${v.id}`}
                className={`w-full relative rounded-xl bg-gradient-to-br ${v.grad} p-2 text-white text-center hover:scale-[1.04] active:scale-95 transition-transform disabled:opacity-60`}>
                {busy === v.id ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : <div className="text-xl leading-none">{v.emoji}</div>}
                <div className="text-[9px] font-semibold mt-1 leading-tight">{v.label}</div>
              </button>
              <div data-testid={`quickfx-vibe-preview-${v.id}`}
                className="pointer-events-none absolute z-30 bottom-full left-1/2 -translate-x-1/2 mb-2 w-40 rounded-xl overflow-hidden shadow-xl border border-black/10 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className={`bg-gradient-to-b ${v.grad} h-16 grid place-items-center text-3xl`}>{v.emoji}</div>
                <div className="bg-[hsl(var(--card))] p-2">
                  <div className="text-[10px] font-bold mb-1">{v.label}</div>
                  <div className="flex flex-wrap gap-1">
                    {v.fx.map((f, i) => <span key={i} className="text-[9px] rounded-full bg-[hsl(var(--secondary))] px-1.5 py-0.5">{f}</span>)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Custom vibes */}
        <div className="mt-3 rounded-xl border border-[hsl(var(--border))] p-2.5">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5 text-xs font-bold"><Bookmark className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> My Vibes</div>
            <a href="/app/vibes" data-testid="quickfx-gallery-link"
              className="text-[10px] font-semibold text-[hsl(var(--primary))] hover:underline">Browse gallery →</a>
          </div>
          {customVibes.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {customVibes.map((v) => (
                <div key={v.id} className="group/cv relative inline-flex items-center">
                  <button onClick={() => call("vibe", { custom_id: v.id }, `cv-${v.id}`)} disabled={!!busy} data-testid={`quickfx-customvibe-${v.id}`}
                    className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--border))] pl-2.5 pr-12 py-1 text-[11px] font-medium hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/5 transition-colors disabled:opacity-60">
                    {busy === `cv-${v.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <span>{v.emoji}</span>} {v.name}
                  </button>
                  <button onClick={() => shareVibe(v.id)} title="Share this vibe" data-testid={`quickfx-customvibe-share-${v.id}`}
                    className="absolute right-6 opacity-0 group-hover/cv:opacity-100 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-opacity">
                    {shareBusy === v.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" />}
                  </button>
                  <button onClick={() => deleteVibe(v.id)} title="Delete" data-testid={`quickfx-customvibe-del-${v.id}`}
                    className="absolute right-1.5 opacity-0 group-hover/cv:opacity-100 text-[hsl(var(--muted-foreground))] hover:text-rose-500 transition-opacity">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <input value={vibeName} onChange={(e) => setVibeName(e.target.value)} placeholder="Name this look…" maxLength={40}
              data-testid="quickfx-savevibe-name"
              className="flex-1 min-w-0 rounded-full border border-[hsl(var(--border))] bg-transparent px-2.5 py-1 text-[11px] focus:outline-none focus:border-[hsl(var(--primary))]/60" />
            <button onClick={saveVibe} disabled={savingVibe} data-testid="quickfx-savevibe-btn"
              className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--primary))] text-white text-[11px] font-semibold px-2.5 py-1 hover:opacity-90 disabled:opacity-60 shrink-0">
              {savingVibe ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save current
            </button>
          </div>
        </div>
      </section>

      <section>
        <h3 className="flex items-center gap-1.5 text-sm font-bold mb-2"><Palette className="w-4 h-4 text-[hsl(var(--primary))]" /> Art Style</h3>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">Restyle the whole world in one tap.</p>
        <label className="flex items-center gap-2 mb-2 text-[11px] font-medium cursor-pointer">
          <input type="checkbox" checked={redraw} onChange={(e) => setRedraw(e.target.checked)} data-testid="quickfx-redraw-toggle" className="accent-[hsl(var(--primary))]" />
          Also repaint the cast — hero, boss, enemy &amp; NPCs — in the new style <span className="text-[hsl(var(--muted-foreground))]">(slower, 2D)</span>
        </label>
        <div className="flex flex-wrap gap-1.5">
          {STYLES.map((s) => (
            <button key={s.id} onClick={() => (redraw ? previewRestyle(s.id) : call("restyle", { style: s.id, redraw: false }, `style-${s.id}`))} disabled={!!busy || !!previewBusy} data-testid={`quickfx-style-${s.id}`}
              className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--border))] px-2.5 py-1 text-xs font-medium hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/5 transition-colors disabled:opacity-60">
              {busy === `style-${s.id}` || previewBusy === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <span>{s.emoji}</span>} {s.label}
            </button>
          ))}
        </div>
        {redraw && (
          <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1.5">Tap a style to preview the hero repaint before you commit the whole cast. 👁️</p>
        )}
        {preview && preview.scope === "cast" ? (
          <div className="mt-3 rounded-xl border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5 p-3" data-testid="quickfx-restyle-preview">
            <div className="text-[11px] font-bold mb-2 capitalize">{preview.style} — whole cast preview</div>
            <div className="space-y-2 max-h-52 overflow-y-auto" data-testid="quickfx-cast-preview">
              {(preview.cast || []).map((m, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`quickfx-cast-${m.role}-${i}`}>
                  <div className="w-12 h-12 rounded-lg bg-white grid place-items-center overflow-hidden border border-[hsl(var(--border))] shrink-0">
                    {m.before ? <img src={m.before} alt="before" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.innerHTML = "<span class='text-lg'>🎭</span>"; }} /> : <span className="text-lg">🎭</span>}
                  </div>
                  <span className="text-[hsl(var(--primary))]">→</span>
                  <div className="w-12 h-12 rounded-lg bg-white grid place-items-center overflow-hidden border-2 border-[hsl(var(--primary))] shrink-0">
                    <img src={m.after} alt="after" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.innerHTML = "<span class='text-lg'>🎭</span>"; }} />
                  </div>
                  <span className="text-[11px] font-medium truncate">{m.label}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={applyPreview} disabled={!!busy} data-testid="quickfx-preview-apply"
                className="flex-1 rounded-full bg-[hsl(var(--primary))] text-white text-xs font-semibold py-1.5 hover:opacity-90 disabled:opacity-60">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : `Apply to all ${(preview.cast || []).length}`}
              </button>
              <button onClick={() => setPreview(null)} data-testid="quickfx-preview-cancel"
                className="rounded-full border border-[hsl(var(--border))] text-xs font-semibold px-3 py-1.5 hover:bg-[hsl(var(--secondary))]">Cancel</button>
            </div>
          </div>
        ) : preview && (
          <div className="mt-3 rounded-xl border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5 p-3" data-testid="quickfx-restyle-preview">
            <div className="text-[11px] font-bold mb-2 capitalize">{preview.style} repaint preview</div>
            <div className="flex items-center justify-center gap-3">
              <div className="text-center">
                <div className="w-20 h-20 rounded-lg bg-white grid place-items-center overflow-hidden border border-[hsl(var(--border))]">
                  {preview.before ? <img src={preview.before} alt="before" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.innerHTML = `<span class='text-3xl'>${preview.emoji}</span>`; }} /> : <span className="text-3xl">{preview.emoji}</span>}
                </div>
                <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Before</div>
              </div>
              <span className="text-[hsl(var(--primary))] text-lg">→</span>
              <div className="text-center">
                <div className="w-20 h-20 rounded-lg bg-white grid place-items-center overflow-hidden border-2 border-[hsl(var(--primary))]">
                  <img src={preview.after} alt="after" className="w-full h-full object-contain" data-testid="quickfx-preview-after" onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.innerHTML = `<span class='text-3xl'>${preview.emoji}</span>`; }} />
                </div>
                <div className="text-[10px] font-semibold text-[hsl(var(--primary))] mt-1">After</div>
              </div>
            </div>
            <button onClick={() => previewRestyle(preview.style, "cast")} disabled={!!previewBusy} data-testid="quickfx-preview-cast-btn"
              className="w-full mt-2 text-[10px] font-semibold text-[hsl(var(--primary))] hover:underline disabled:opacity-50">
              {previewBusy ? "Repainting the whole cast…" : "👥 Preview the whole cast instead"}
            </button>
            <div className="flex gap-2 mt-2">
              <button onClick={applyPreview} disabled={!!busy} data-testid="quickfx-preview-apply"
                className="flex-1 rounded-full bg-[hsl(var(--primary))] text-white text-xs font-semibold py-1.5 hover:opacity-90 disabled:opacity-60">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : "Apply to whole cast"}
              </button>
              <button onClick={() => setPreview(null)} data-testid="quickfx-preview-cancel"
                className="rounded-full border border-[hsl(var(--border))] text-xs font-semibold px-3 py-1.5 hover:bg-[hsl(var(--secondary))]">Cancel</button>
            </div>
          </div>
        )}
        <div className="mt-2 flex items-center gap-3">
          <button onClick={() => exportCastSheet(false)} disabled={!!castBusy} data-testid="quickfx-cast-sheet-btn"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--primary))] hover:underline disabled:opacity-50">
            {castBusy === "sheet" ? <><Loader2 className="w-3 h-3 animate-spin" /> Building…</> : "📸 Export cast line-up"}
          </button>
          <button onClick={() => exportCastSheet(true)} disabled={!!castBusy} data-testid="quickfx-cast-poster-btn"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--primary))] hover:underline disabled:opacity-50">
            {castBusy === "poster" ? <><Loader2 className="w-3 h-3 animate-spin" /> Building…</> : "🖼️ Export promo poster"}
          </button>
        </div>
      </section>

      <section>
        <h3 className="flex items-center gap-1.5 text-sm font-bold mb-2"><Wand2 className="w-4 h-4 text-[hsl(var(--primary))]" /> Genre Packs</h3>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">One tap layers in a whole genre — systems, enemies &amp; controls.</p>
        {suggestion && (
          <button onClick={() => setPackPreview(GENRE_PACKS.find((p) => p.id === suggestion.pack) || null)} data-testid="quickfx-suggest-pack"
            className="w-full text-left mb-2 rounded-xl border border-[hsl(var(--primary))]/40 bg-gradient-to-r from-[#a855f7]/10 to-[#ec4899]/10 p-2.5 hover:from-[#a855f7]/15 hover:to-[#ec4899]/15 transition-colors">
            <div className="text-[11px] font-bold flex items-center gap-1">✨ Studio suggests: <span>{suggestion.emoji} {suggestion.label}</span></div>
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">{suggestion.reason}</div>
          </button>
        )}
        <div className="flex flex-wrap gap-1.5" data-testid="quickfx-genre-packs">
          {GENRE_PACKS.map((p) => (
            <button key={p.id} onClick={() => setPackPreview(packPreview?.id === p.id ? null : p)} disabled={!!busy} data-testid={`quickfx-genre-${p.id}`}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-60 ${packPreview?.id === p.id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/5"}`}>
              {busy === `genre-${p.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <span>{p.emoji}</span>} {p.label}
            </button>
          ))}
        </div>
        {packPreview && (
          <div className="mt-3 rounded-xl border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5 p-3" data-testid="quickfx-pack-preview">
            <div className="text-[11px] font-bold mb-1.5">{packPreview.emoji} Turning this into a {packPreview.label} adds:</div>
            <ul className="space-y-0.5 mb-3">
              {packPreview.changes.map((ch, i) => (
                <li key={i} className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-start gap-1.5"><span className="text-[hsl(var(--primary))]">+</span> {ch}</li>
              ))}
            </ul>
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mb-2">You can undo it in one tap afterwards. ↩️</p>
            <div className="flex gap-2">
              <button onClick={() => genrePack(packPreview.id, packPreview.id)} disabled={!!busy} data-testid="quickfx-pack-apply"
                className="flex-1 rounded-full bg-[hsl(var(--primary))] text-white text-xs font-semibold py-1.5 hover:opacity-90 disabled:opacity-60">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : `Apply ${packPreview.label} pack`}
              </button>
              <button onClick={() => setPackPreview(null)} data-testid="quickfx-pack-cancel"
                className="rounded-full border border-[hsl(var(--border))] text-xs font-semibold px-3 py-1.5 hover:bg-[hsl(var(--secondary))]">Cancel</button>
            </div>
          </div>
        )}
      </section>

      <section>
        <h3 className="flex items-center gap-1.5 text-sm font-bold mb-2"><Package className="w-4 h-4 text-[hsl(var(--primary))]" /> Object Palette</h3>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">Tap to drop items, power-ups &amp; hazards into your game.</p>
        <div className="grid grid-cols-4 gap-1.5">
          {OBJECTS.map((o) => (
            <button key={o.id} onClick={() => call("object", { object: o.id }, `obj-${o.id}`)} disabled={!!busy} data-testid={`quickfx-object-${o.id}`}
              className="rounded-xl border border-[hsl(var(--border))] p-2 text-center hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/5 transition-colors disabled:opacity-60">
              {busy === `obj-${o.id}` ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : <div className="text-xl leading-none">{o.emoji}</div>}
              <div className="text-[9px] font-medium mt-1 leading-tight">{o.label}</div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
